import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import LogFoodHub from './logfood/LogFoodHub';
import RestaurantMenuView from './logfood/RestaurantMenuView';
import FoodConfirm from './logfood/FoodConfirm';
import BottomNav from '../components/BottomNav';
import BasketBar from '../components/logfood/BasketBar';
import { resolveLoggingDate } from '../utils/dateUtils';
import { useToday } from '../hooks/useToday';
import { logFoodAPI } from '../utils/api';
import { searchItemToFood, mergeHydratedFood, recentFoodToFood, isGroupedRecent, groupedBasketTitle, basketComponentMicros, groupedMicroTotals } from '../utils/logFoodMappers';
import { runLeaveLogMeal, runDiscardAndLeave, ensureBackGuard, makeBackPopHandler } from '../utils/basketNavGuard';
import { logServingDebug } from '../utils/servingDebug';

/**
 * LogFood — entry container for the Log Food feature.
 *
 * Phase 1 (Feb 2026): replaces the 2-tile radial menu with a 5-tab hub
 * (Foods · Restaurants · Meals · Barcode · Manual). FatSecret-dependent
 * tabs hide themselves when REACT_APP_ENABLE_FATSECRET=false. Existing
 * FoodConfirm flow and existing /add-custom-meal builder are reused
 * unchanged.
 */
function LogFood() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ONE reactive local-day source, shared with Home. `?current=1` (or the absence of an
  // explicit ?date=) marks a LIVE current-day flow → use the reactive `useToday()` value so a
  // log after local midnight lands on the new day. An explicit ?date= (Progress / PlanningHub
  // calendar/history) is an INTENTIONALLY fixed date and is preserved as-is.
  // useToday() is called UNCONDITIONALLY (hook rules); we then select which value to use.
  const liveToday = useToday();
  const explicitDate = searchParams.get('date');
  const isCurrent = searchParams.get('current') === '1' || !explicitDate;
  const date = resolveLoggingDate({
    explicitDate, currentFlag: searchParams.get('current'), liveToday, missingMeansCurrent: true,
  });
  const mealType = searchParams.get('type') || 'Snack';
  const returnPath = searchParams.get('return') || '/home';

  // currentView: hub | confirm | restaurant_menu
  const [currentView, setCurrentView] = useState('hub');
  const [selectedFood, setSelectedFood] = useState(null);
  const [selectedChain, setSelectedChain] = useState(null);
  const [confirmSourceView, setConfirmSourceView] = useState(null);
  // True while we fetch full FatSecret details for a selected search hit.
  const [selecting, setSelecting] = useState(false);

  // Multi-food selection basket (the `+` on result rows adds here instead of
  // logging immediately). Only cleared on a successful commit or explicit clear.
  const [basket, setBasket] = useState([]);
  const [committingBasket, setCommittingBasket] = useState(false);
  const committingRef = useRef(false);

  // Unsaved-cart navigation guard: when the cart has unlogged foods and the user tries to leave the
  // Log Food flow, we intercept the navigation, remember the destination, and prompt.
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);
  const [postLogDestination, setPostLogDestination] = useState(null);

  const addToBasket = (food) => {
    if (!food) return;
    const key = (food.source || '') + '::' + (food.external_id || '') + '::' + (food.name || '');
    setBasket((prev) => {
      if (prev.some((it) => it._basketKey === key)) {
        toast('Already added');
        return prev;
      }
      toast.success('Added ' + (food.name || 'food'));
      return prev.concat([Object.assign({}, food, { _basketKey: key })]);
    });
  };

  const removeFromBasket = (key) => setBasket((prev) => prev.filter((it) => it._basketKey !== key));
  const clearBasket = () => setBasket([]);

  // Single centralized guard for every outbound navigation from the Log Food flow. Returns false
  // (and shows the prompt) when the cart has unlogged foods; otherwise navigates and returns true.
  // Internal view switches (hub <-> confirm <-> restaurant menu) do NOT call this — they only flip
  // `currentView` and never change the route — so the prompt never fires mid-flow.
  const attemptLeave = (dest) => {
    const target = dest || returnPath;
    if (basket.length > 0) {
      setPendingNav(target);
      setShowLeaveDialog(true);
      return false;
    }
    navigate(target);
    return true;
  };

  // Open a GROUPED recent (AI photo / basket meal) in the grouped CustomMealView
  // detail — combined macros + component rows + FatSecret ids preserved — where
  // "Log Again" creates exactly ONE grouped row. Never flatten it to FoodConfirm.
  const openGroupedRecent = (recent) => {
    const mealId = recent.external_id || recent.meal_id;
    if (!mealId) return;
    const m = recent.macros || {};
    const params = new URLSearchParams();
    // Recent history is used only to hydrate/re-log. It must NOT masquerade as
    // an already-logged row for the currently selected date, so we do not carry
    // the recent's plan_id or force status=completed (that plan row may belong
    // to another date). The re-log writes to the resolved logging date below.
    params.set('type', recent.meal_type || mealType || 'Snack');
    params.set('date', date);
    if (isCurrent) params.set('current', '1');   // carry live current-day intent into CustomMealView
    params.set('return', returnPath);
    if (recent.name) params.set('name', recent.name);
    params.set('calories', String(Math.round(m.calories || 0)));
    params.set('protein', String(Math.round(m.protein || 0)));
    params.set('carbs', String(Math.round(m.carbs || 0)));
    params.set('fat', String(Math.round(m.fat || 0)));
    attemptLeave('/meal-view/' + mealId + '?' + params.toString() + '&view=grouped');
  };

  // Recents: grouped -> CustomMealView; normal FatSecret/restaurant/manual ->
  // convert to the per-unit FoodConfirm shape (recentFoodToFood) so macros are
  // never zero. Tapping the row reviews in FoodConfirm; the [+] adds to the basket.
  const handleSelectRecent = (recent) => {
    if (isGroupedRecent(recent)) { openGroupedRecent(recent); return; }
    handleSelectFood(recentFoodToFood(recent));
  };
  const handleQuickLogRecent = (recent) => {
    if (isGroupedRecent(recent)) { openGroupedRecent(recent); return; }
    addToBasket(recentFoodToFood(recent));
  };

  // 1 item -> the SAME single-food final log operation FoodConfirm uses, run
  // directly from the basket (no FoodConfirm round-trip). `food.nutrition` is
  // per-unit and `serving_size` is the active amount, so we log qty = the
  // active amount against the active unit. Basket is cleared only on success.
  const commitBasketSingle = (food, destination) => {
    if (committingRef.current) return;
    const qty = (typeof food.serving_size === 'number' && food.serving_size > 0) ? food.serving_size : 1;
    setCommittingBasket(true);
    committingRef.current = true;
    logFoodAPI.logFood(food, date, mealType, qty)
      .then(() => {
        clearBasket();
        toast.success((food.name || 'Food') + ' logged!');
        navigate(destination || returnPath);
      })
      .catch(() => {
        // Do NOT clear the basket on failure.
        toast.error('Failed to log food. Please try again.');
      })
      .finally(() => {
        setCommittingBasket(false);
        committingRef.current = false;
      });
  };

  // Update a basket item's active amount/unit (serving controls in BasketBar).
  const updateBasketItem = (key, patch) => {
    setBasket((prev) => prev.map((it) => (it._basketKey === key ? Object.assign({}, it, patch) : it)));
  };

  // 2+ items -> one grouped custom meal (single atomic /log write). Basket is
  // cleared ONLY after the write succeeds. `destination` lets the leave-guard continue to the page
  // the user originally tried to open; defaults to the flow's returnPath for the normal Log button.
  const commitBasketGroup = async (items, destination) => {
    // Duplicate-submission guard: ignore re-entry while a commit is already in flight.
    if (committingRef.current) return;
    const components = items.map((it) => {
      const n = it.nutrition || {};
      const qty = (typeof it.serving_size === 'number' && it.serving_size > 0) ? it.serving_size : 1;
      return Object.assign({
        name: it.name,
        food_id: it.external_id || null,
        quantity: qty,
        unit: it.serving_unit || 'serving',
        calories: Math.round((n.calories || 0) * qty),
        protein: Math.round((n.protein_g || 0) * qty * 10) / 10,
        carbs: Math.round((n.carbs_g || 0) * qty * 10) / 10,
        fat: Math.round((n.fat_g || 0) * qty * 10) / 10,
        external_id: it.external_id || null,
        brand: it.brand || null,
        restaurant: it.restaurant || null,
        serving_description: it.serving_description || null,
      }, basketComponentMicros(n, qty));   // known sodium/sugar/fiber only
    });
    const totals = components.reduce((a, c) => ({
      calories: a.calories + c.calories,
      protein_g: Math.round((a.protein_g + c.protein) * 10) / 10,
      carbs_g: Math.round((a.carbs_g + c.carbs) * 10) / 10,
      fat_g: Math.round((a.fat_g + c.fat) * 10) / 10,
    }), { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 });
    // Grouped micro totals are all-or-nothing per nutrient (never a partial sum).
    Object.assign(totals, groupedMicroTotals(components));
    const names = components.map((c) => c.name);
    const title = groupedBasketTitle(components);
    const groupedFood = {
      external_id: 'grouped_' + Date.now(),
      name: title,
      image_url: null,
      serving_size: 1,
      serving_unit: 'serving',
      nutrition: totals,
      source: 'CUSTOM',
      ingredients: names,
      ingredient_details: components,
      serving_options: [],
    };
    setCommittingBasket(true);
    committingRef.current = true;
    try {
      await logFoodAPI.logFood(groupedFood, date, mealType, 1);
      clearBasket();
      toast.success('Logged ' + names.length + ' foods as one meal');
      navigate(destination || returnPath);
    } catch (e) {
      // Do NOT clear the basket on failure.
      toast.error('Failed to log meal. Please try again.');
    } finally {
      setCommittingBasket(false);
      committingRef.current = false;
    }
  };

  // Open the existing manual-entry flow with an optional prefilled name.
  // Builds a blank manual_<ts> food and routes straight to FoodConfirm —
  // preserves the long-standing behavior from the prior LogFood.jsx.
  const openManualEntry = (prefillName) => {
    setSelectedFood({
      external_id: 'manual_' + Date.now(),
      name: prefillName || 'Custom Food',
      nutrition: { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
      serving_unit: 'serving',
      serving_size: 1,
      serving_options: [],
      source: 'MANUAL',
      ingredients: [],
    });
    setConfirmSourceView(currentView);
    setCurrentView('confirm');
  };

  const handleSelectFood = async (food) => {
    logServingDebug('foodEntersLogFood', food && {
      external_id: food.external_id, source: food.source, brand: food.brand,
      serving_size: food.serving_size, serving_unit: food.serving_unit,
      serving_description: food.serving_description, nutrition: food.nutrition,
      serving_options: food.serving_options,
    });
    // FatSecret Basic-tier search results arrive with 0-1 serving_options
    // (no servings array from foods.search). Fetch full details so the unit
    // picker shows every serving option — same data barcode lookup gets.
    let resolved = food;
    const ext = food && food.external_id;
    const needsDetails =
      food &&
      food.source === 'FATSECRET' &&
      (!Array.isArray(food.serving_options) || food.serving_options.length <= 1) &&
      typeof ext === 'string' &&
      (ext.startsWith('fs_food_') || ext.startsWith('fs_branded_') || ext.startsWith('fs_restaurant_'));
    if (needsDetails) {
      setSelecting(true);
      try {
        const res = await logFoodAPI.getFoodDetails(ext);
        const item = res && res.data && res.data.item;
        if (item) {
          // Merge any returned detail — even a single serving (or a 0-cal item)
          // can carry sodium/sugar/fiber. mergeHydratedFood keeps known macros
          // and the known active serving, so detail can never zero them out.
          resolved = mergeHydratedFood(food, searchItemToFood(item));
          // Restaurant items hydrate through the SAME food.get path, but
          // map_food_to_search_item re-prefixes the id to fs_food_/fs_branded_
          // and drops the restaurant label. Re-assert the selected restaurant
          // identity so the Confirm screen still shows the chain and the log
          // is attributed to it.
          if (ext.startsWith('fs_restaurant_')) {
            resolved = {
              ...resolved,
              external_id: food.external_id,
              name: food.name,
              restaurant: food.restaurant || null,
              brand: null,
              source: food.source,
            };
          }
        }
      } catch (e) {
        // Non-fatal: keep the known list/recent item (which already carries
        // per-unit nutrition). Confirm still works with accurate macros; the
        // picker just won't gain extra serving units.
        console.error('[LogFood] getFoodDetails failed; using known item:', e && e.message);
      } finally {
        setSelecting(false);
      }
    }
    setSelectedFood(resolved);
    logServingDebug('foodToFoodConfirm', resolved && {
      external_id: resolved.external_id, source: resolved.source, brand: resolved.brand,
      serving_size: resolved.serving_size, serving_unit: resolved.serving_unit,
      serving_description: resolved.serving_description, nutrition: resolved.nutrition,
      serving_options: resolved.serving_options, hydrated: needsDetails,
    });
    setConfirmSourceView(currentView);
    setCurrentView('confirm');
  };

  const handleOpenChain = (chain) => {
    setSelectedChain(chain);
    setCurrentView('restaurant_menu');
  };

  const handleLogSuccess = () => {
    // A successful single-food log clears any pending basket selection (a
    // failed/cancelled confirmation never reaches here, so the basket is kept).
    clearBasket();
    // If this review was launched from the leave-guard's "Log meal", continue to the destination
    // the user originally requested; otherwise fall back to the flow's returnPath.
    const dest = postLogDestination || returnPath;
    setPostLogDestination(null);
    navigate(dest);
  };

  const handleClose = () => {
    if (currentView === 'confirm') {
      // Closing the single-food confirmation WITHOUT logging must drop any pending leave-guard
      // destination, so a later unrelated log never navigates to the previously attempted page.
      setPostLogDestination(null);
      setCurrentView(confirmSourceView || 'hub');
      setSelectedFood(null);
    } else if (currentView === 'restaurant_menu') {
      setCurrentView('hub');
      setSelectedChain(null);
    } else {
      attemptLeave(returnPath);
    }
  };

  // --- Unsaved-cart leave dialog actions ---
  const leaveKeepEditing = () => {
    setShowLeaveDialog(false);
    setPendingNav(null);
  };
  const leaveDiscard = () => {
    const dest = pendingNav || returnPath;
    setShowLeaveDialog(false);
    setPendingNav(null);
    runDiscardAndLeave({ clearBasket, navigate, destination: dest });
  };
  const leaveLogMeal = () => {
    const dest = pendingNav || returnPath;
    setShowLeaveDialog(false);
    setPendingNav(null);
    runLeaveLogMeal({
      basket,
      destination: dest,
      commitGroup: commitBasketGroup,
      commitSingle: (food, d) => { commitBasketSingle(food, d || dest); },
    });
  };

  // Device / browser back protection for an unlogged cart.
  //
  // Lifecycle: push EXACTLY ONE marked history entry (`__fitxessLogFoodGuard`) the first time the
  // cart becomes non-empty. We never push a second one (the marker on `history.state` is the source
  // of truth, so add → clear → add cannot accumulate entries). On Back:
  //   - cart non-empty  → re-assert the marked entry and show the leave prompt (blocks the exit);
  //   - cart empty      → allow the Back (no re-push, no prompt) — so after discard/successful log
  //                       the user leaves in a single Back press and the dialog never reopens.
  const basketLenRef = useRef(basket.length);
  useEffect(() => { basketLenRef.current = basket.length; }, [basket.length]);

  useEffect(() => {
    if (basket.length === 0) return;
    ensureBackGuard(window);
  }, [basket.length]);

  useEffect(() => {
    const onPop = makeBackPopHandler(window, () => basketLenRef.current, () => {
      setPendingNav(returnPath);
      setShowLeaveDialog(true);
    });
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [returnPath]);

  // Scroll reset (Bug 2): the SPA keeps window.scrollY across route changes,
  // so opening Log Food while Home is scrolled down landed the user mid-page
  // (header/photo-tile/tabs off-screen). Reset to the top on entry and on
  // every in-page view switch. Scoped to this screen — no global ScrollToTop.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentView]);

  // Legacy cross-component event hook. Older code paths emit
  // 'open-food-search' to land on the Log Food search UI. With the new
  // hub there's nothing extra to do — the hub IS the search UI — so we
  // just ensure we're showing the hub view.
  useEffect(() => {
    const handleOpenSearch = () => {
      setCurrentView('hub');
    };
    window.addEventListener('open-food-search', handleOpenSearch);
    return () => window.removeEventListener('open-food-search', handleOpenSearch);
  }, []);

  return (
    <div className="min-h-screen bg-background pb-bottom-nav" data-testid="log-food-page">
      {currentView === 'hub' && (
        <LogFoodHub
          date={date}
          mealType={mealType}
          onClose={handleClose}
          onSelectFood={handleSelectFood}
          onAddToBasket={addToBasket}
          onSelectRecent={handleSelectRecent}
          onQuickLogRecent={handleQuickLogRecent}
          onOpenChain={handleOpenChain}
          onOpenManualEntry={openManualEntry}
        />
      )}

      {currentView === 'restaurant_menu' && selectedChain && (
        <RestaurantMenuView
          chain={selectedChain}
          onBack={handleClose}
          onSelectFood={handleSelectFood}
          onQuickLog={addToBasket}
        />
      )}

      {currentView === 'confirm' && selectedFood && (
        <FoodConfirm
          food={selectedFood}
          date={date}
          mealType={mealType}
          onClose={handleClose}
          onSuccess={handleLogSuccess}
        />
      )}

      {currentView !== 'confirm' && (
        <BasketBar
          items={basket}
          onRemove={removeFromBasket}
          onClear={clearBasket}
          onUpdateItem={updateBasketItem}
          onCommitSingle={commitBasketSingle}
          onCommitGroup={commitBasketGroup}
          committing={committingBasket}
        />
      )}

      {currentView !== 'confirm' && <BottomNav beforeNavigate={attemptLeave} />}

      {showLeaveDialog && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center sm:justify-center" data-testid="leave-cart-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={leaveKeepEditing} data-testid="leave-cart-backdrop" />
          <div className="relative w-full sm:max-w-sm bg-background rounded-t-2xl sm:rounded-2xl p-5 shadow-xl" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.25rem)' }}>
            <h2 className="text-base font-bold text-foreground" data-testid="leave-cart-title">Log this meal before leaving?</h2>
            <p className="text-sm text-muted-foreground mt-1" data-testid="leave-cart-body">
              You have food in your meal that hasn’t been logged yet.
            </p>
            <div className="flex flex-col gap-2 mt-4">
              <button
                type="button"
                onClick={leaveLogMeal}
                disabled={committingBasket}
                className="h-11 rounded-xl bg-accent text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="leave-cart-log"
              >
                {committingBasket ? <Loader2 size={16} className="animate-spin" /> : null}
                Log meal
              </button>
              <button type="button" onClick={leaveKeepEditing} className="h-11 rounded-xl border border-border/50 text-sm font-medium text-foreground" data-testid="leave-cart-keep">Keep editing</button>
              <button type="button" onClick={leaveDiscard} className="h-11 rounded-xl text-sm font-medium text-destructive" data-testid="leave-cart-discard">Discard and leave</button>
            </div>
          </div>
        </div>
      )}

      {selecting && (
        <div
          className="fixed inset-0 z-[60] bg-black/20 flex items-center justify-center"
          data-testid="food-details-loading"
        >
          <div className="bg-white rounded-2xl px-6 py-4 shadow-lg flex items-center gap-3">
            <Loader2 className="size-5 animate-spin text-accent" />
            <span className="text-sm text-gray-700">Loading options…</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default LogFood;
